import romanApi from "@roman/shared/lib/net/romanApi";
import type { PredictionMarketWithProbability } from "@roman/shared/types/PredictionMarket";
import queryString from "query-string";
import useSWRInfinite, { type SWRInfiniteConfiguration } from "swr/infinite";
import type { Meta } from "../../types/Meta";

const DEFAULT_PER_PAGE = 10;

export interface GetPublicPredictionMarketsResponse {
  predictionMarkets: (PredictionMarketWithProbability & {
    totalEnteredFreePoints: number;
    numberOfParticipants: number;
  })[];
  meta: Meta;
}

interface UseGetPublicPredictionMarketsProps {
  orderBy?: "creation" | "popularity";
  swrOptions?: SWRInfiniteConfiguration;
  fetcher?: (url: string) => Promise<GetPublicPredictionMarketsResponse>;
  label?: string;
  subLabel?: string;
  search?: string;
  type?: string;
}

const getPublicPredictionMarketsFetcher = async (url: string): Promise<GetPublicPredictionMarketsResponse> => {
  return romanApi.get<GetPublicPredictionMarketsResponse>(url);
};

const getKey = (
  pageIndex: number,
  previousPageData: GetPublicPredictionMarketsResponse | null,
  orderBy?: string,
  label?: string,
  subLabel?: string,
  search?: string,
  type?: string,
) => {
  if (previousPageData && !previousPageData.predictionMarkets?.length) {
    return null; // reached the end
  }

  const qs = queryString.stringify({
    status: "Open",
    per: DEFAULT_PER_PAGE,
    ...(label ? {} : { orderBy }),
    label,
    subLabel,
    page: pageIndex + 1,
    search,
    type,
  });

  return `/prediction-market/?${qs}`;
};

export const useGetPublicPredictionMarkets = ({
  orderBy,
  swrOptions,
  fetcher,
  label,
  search,
  subLabel,
  type,
}: UseGetPublicPredictionMarketsProps = {}) => {
  const entriesFetcher = fetcher || getPublicPredictionMarketsFetcher;

  const options: SWRInfiniteConfiguration = {
    revalidateFirstPage: false,
    ...swrOptions,
  };

  const swrQuery = useSWRInfinite<GetPublicPredictionMarketsResponse>(
    (pageIndex, previousPageData) => getKey(pageIndex, previousPageData, orderBy, label, subLabel, search, type),
    (url: string) => entriesFetcher(url),
    options,
  );

  const predictionMarkets = swrQuery.data ? swrQuery.data.flatMap((pageData) => pageData.predictionMarkets) : [];

  return {
    ...swrQuery,
    predictionMarkets,
    page: swrQuery.size,
    perPage: DEFAULT_PER_PAGE,
    setPage: swrQuery.setSize,
  };
};
